// The browser half of account sync: who is signed in, and the save slots the
// server holds for them.
//
// Every call here is allowed to fail. The game keeps its own copy of the farm in
// IndexedDB, so a server that is slow, down, or simply not configured costs the
// player nothing — the sync tier goes quiet and the local one carries on.
import type { SaveEnvelope } from "./save.ts";

export interface AccountUser {
  id: string;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export interface Account {
  signedIn: boolean;
  /** false when the server runs without a configured auth client (dev, e2e) */
  authRequired: boolean;
  /** whether the server can actually store saves */
  sync: boolean;
  user?: AccountUser;
  loginUrl?: string;
}

export interface CloudSlot {
  slot: number;
  savedAt: number;
  day: number;
  gold: number;
  season: string;
}

const jsonHeaders = { "content-type": "application/json" };

export async function fetchAccount(): Promise<Account> {
  try {
    const res = await fetch("/api/me", { credentials: "same-origin" });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as Account;
  } catch {
    // No server (a bare `file://` open, or an outage mid-session): treat it as
    // a signed-out, sync-less world rather than a dead one.
    return { signedIn: false, authRequired: false, sync: false, loginUrl: "/auth/login" };
  }
}

export async function signOut(): Promise<void> {
  try {
    await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
  } catch {
    /* the reload below sends them to the sign-in screen regardless */
  }
}

/**
 * The remote save slots. Constructed only when the server reports sync is on;
 * `SaveStore` treats it as an optional second tier behind the local one.
 */
export class CloudSaves {
  /** slots whose last push failed, retried on the next autosave */
  private readonly dirty = new Map<number, SaveEnvelope>();

  async list(): Promise<CloudSlot[]> {
    try {
      const res = await fetch("/api/saves", { credentials: "same-origin" });
      if (!res.ok) return [];
      const j = (await res.json()) as { slots?: CloudSlot[] };
      return j.slots ?? [];
    } catch {
      return [];
    }
  }

  async read(slot: number): Promise<SaveEnvelope | null> {
    try {
      const res = await fetch(`/api/saves/${slot}`, { credentials: "same-origin" });
      if (!res.ok) return null;
      const j = (await res.json()) as { data: Record<string, unknown>; savedAt: number };
      return { slot, savedAt: j.savedAt, data: j.data };
    } catch {
      return null;
    }
  }

  /** Push a slot, remembering it for a retry if the write does not land. */
  async write(env: SaveEnvelope): Promise<boolean> {
    this.dirty.set(env.slot, env);
    try {
      const res = await fetch(`/api/saves/${env.slot}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: jsonHeaders,
        body: JSON.stringify({ data: env.data, savedAt: env.savedAt }),
      });
      if (!res.ok) return false;
      // Only clear if nothing newer arrived while this was in flight.
      if (this.dirty.get(env.slot)?.savedAt === env.savedAt) this.dirty.delete(env.slot);
      return true;
    } catch {
      return false;
    }
  }

  async retryDirty(): Promise<void> {
    for (const env of [...this.dirty.values()]) await this.write(env);
  }

  /**
   * The last-gasp push as the tab goes away. `fetch` is cancelled on unload;
   * `sendBeacon` is queued by the browser and survives it — which is why the
   * server accepts POST as well as PUT on a slot.
   */
  flush(env: SaveEnvelope): void {
    try {
      const body = new Blob([JSON.stringify({ data: env.data, savedAt: env.savedAt })], {
        type: "application/json",
      });
      navigator.sendBeacon(`/api/saves/${env.slot}`, body);
    } catch {
      /* nothing further to try at this point in the tab's life */
    }
  }

  async remove(slot: number): Promise<void> {
    this.dirty.delete(slot);
    try {
      await fetch(`/api/saves/${slot}`, { method: "DELETE", credentials: "same-origin" });
    } catch {
      /* the local copy is gone; the remote one loses on next compare anyway */
    }
  }
}
